/**
 * Client for handling asset transformations in FireTV Campaign Automation
 */
import {
  AssetType,
  FireTVCampaignAutomationClient,
  TransformAssetInput,
  TransformationVariant,
  Placement,
} from '@amzn/fire-tv-campaign-automation-service';
import { getValidationProps } from 'src/configs/assetConfig';
import { getTransformationTriggerFields } from 'src/configs/logoTextInputConfig';
import { Poller } from 'src/constants';
import { ToastContextType } from 'src/contexts/ToastContext';
import { logger } from 'src/logger';
import { metricsPublisher } from 'src/metrics';
import { AssetState, AssetStatus, METRIC_NAME, DIMENSION_KEYS } from 'src/types';
import { transformAsset } from 'src/utils/clients/controlPlane/apiMethods';
import { executePoll } from 'src/utils/Poller';
import { ASSET_TRANSFORMATION_STATUS_MESSAGES } from 'src/utils/PollerUtils';
import { sessionContext } from 'src/utils/SessionContext';

/**
 * Props required to initialize AssetTransformationHandler
 */
export interface AssetTransformationHandlerProps {
  /** Callback to update asset state */
  setState: (assetState: Partial<AssetState>) => void;
  /** Client for interacting with FireTV Campaign Automation service */
  controlPlaneClient: FireTVCampaignAutomationClient;
  /** Abort signal to interrupt polling */
  abortSignal?: AbortSignal;
  /** Toast context for notifications */
  toast: ToastContextType;
  /** Placement for validation props */
  placement: Placement;
}

/**
 * Handles transformation of assets including initiating transforms and polling status
 */
export class AssetTransformationHandler {
  private readonly setState: (assetState: Partial<AssetState>) => void;
  private readonly controlPlaneClient: FireTVCampaignAutomationClient;
  private readonly abortSignal?: AbortSignal;
  private readonly toast: ToastContextType;
  private readonly placement: Placement;

  /**
   * Creates a new AssetTransformationHandler instance
   * @param props - Configuration props
   */
  constructor(props: AssetTransformationHandlerProps) {
    this.setState = props.setState;
    this.controlPlaneClient = props.controlPlaneClient;
    this.toast = props.toast;
    this.abortSignal = props.abortSignal;
    this.placement = props.placement;
  }

  /**
   * Initiates asset transformation and monitors status
   * @param transformAssetInput - Input parameters for asset transformation
   */
  //TODO: Add abort signal check at the caller of this method.
  public async handleTransformation(transformAssetInput: TransformAssetInput): Promise<void> {
    logger.debug('Transforming asset', { transformAssetInput });

    const transformationTriggerFields = getTransformationTriggerFields(
      transformAssetInput.transformationVariant,
    );
    const missingLogoFields = transformationTriggerFields.some(
      (field) => !transformAssetInput[field],
    );

    if (transformAssetInput.assetType === AssetType.LOGO && missingLogoFields) {
      logger.warn('Logo transformation skipped due to missing required fields', {
        transformAssetInput,
      });
      return;
    }

    if (transformAssetInput.assetType === AssetType.VIDEO_TRAILER) {
      if (transformAssetInput.transformationVariant === TransformationVariant.CLIPPED_VIDEO) {
        const noTransformationConfig = getValidationProps(
          AssetType.VIDEO_TRAILER,
          this.placement,
          TransformationVariant.NO_TRANSFORMATION,
        );

        if (!noTransformationConfig?.videoLengthSpecs?.maxDurationInSeconds) {
          logger.error('Missing video validation config for placement', {
            placement: this.placement,
          });
          throw new Error(`Missing video validation config for placement ${this.placement}`);
        }

        const maxDuration = noTransformationConfig.videoLengthSpecs.maxDurationInSeconds;
        const isDurationValid =
          transformAssetInput.endTime != null &&
          transformAssetInput.startTime != null &&
          transformAssetInput.endTime - transformAssetInput.startTime <= maxDuration;

        if (!isDurationValid) {
          logger.warn('Video transformation skipped due to invalid duration', {
            transformAssetInput,
          });
          return;
        }
      }
    }

    // TODO: set a new status here and move this state after the transformAsset returns a response.
    this.setState({ status: AssetStatus.TRANSFORMATION_IN_PROGRESS });
    sessionContext.addTransformationVariant(transformAssetInput.transformationVariant);
    const transformStartTime = performance.now();
    metricsPublisher
      .trackLatency(
        METRIC_NAME.TRANSFORMATION_API_LATENCY,
        () => transformAsset(this.controlPlaneClient, transformAssetInput),
        {
          [DIMENSION_KEYS.ASSET_TYPE]: transformAssetInput.assetType,
          [DIMENSION_KEYS.TRANSFORMATION_VARIANT]: transformAssetInput.transformationVariant,
        },
      )
      .then((response) => {
        logger.debug('Transform Asset initiated', {
          transformationId: response.transformationId,
        });
        //TODO: check if the state is already in the required state before polling.
        // Start polling for transformation status
        metricsPublisher.trackLatency(
          METRIC_NAME.TRANSFORMATION_POLL_LATENCY,
          () =>
            executePoll(
              Poller.Transformation,
              response.transformationId,
              this.setState,
              this.controlPlaneClient,
              this.toast,
              this.abortSignal,
              {
                transformStartTime,
                assetType: transformAssetInput.assetType,
                transformationVariant: transformAssetInput.transformationVariant,
              },
            ),
          {
            [DIMENSION_KEYS.ASSET_TYPE]: transformAssetInput.assetType,
            [DIMENSION_KEYS.TRANSFORMATION_VARIANT]: transformAssetInput.transformationVariant,
          },
        );
      })
      .catch((error) => {
        logger.error('Error in transformation', { error });

        metricsPublisher.emitCount(METRIC_NAME.TRANSFORMATION_FAILURE, {
          [DIMENSION_KEYS.ASSET_TYPE]: transformAssetInput.assetType,
          [DIMENSION_KEYS.TRANSFORMATION_VARIANT]: transformAssetInput.transformationVariant,
        });

        this.setState({ status: AssetStatus.FAILED, error: error.message, key: undefined });
        this.toast.addToast({
          variant: 'danger',
          description: ASSET_TRANSFORMATION_STATUS_MESSAGES.error,
        });
      });
  }
}
